import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import moment from "moment";

// ReportsBarChart configurations
import configs from "examples/Charts/BarCharts/ReportsBarChartCombined/configs";

function ReportsBarChart({
  color,
  title,
  description,
  date,
  chart,
  graph,
  dgraph,
  flag,
  totalFlag,
}) {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );
  const { options } = configs(chart || [], chart || {});

  // ddata is daily, mdata is monthly and data is yearly
  const ddata = {
    labels: dgraph?.map((row) => moment(row.date).format("DD/MM")),
    datasets: [
      {
        label: "Image Count",
        data: dgraph?.map((row) => row?.total_image_count),
        backgroundColor: "rgba(53, 162, 235)",
        borderColor: "rgba(53, 162, 235,0.5)",
      },
      {
        labels: dgraph?.map((row) => moment(row.date).format("DD/MM")),
        label: "Text count",
        data: dgraph?.map((row) => row?.total_text_count),
        backgroundColor: "rgba(255, 99, 132)",
        borderColor: "rgba(255, 99, 132,0.5)",
      },
    ],
  };

  const data = {
    labels: graph?.map((row) => moment(row._id).format("MMM")),
    datasets: [
      {
        label: "Image Count",
        data: graph?.map((row) => row?.total_images),
        backgroundColor: "rgba(53, 162, 235)",
        borderColor: "rgba(53, 162, 235,0.5)",
      },
      {
        labels: graph?.map((row) => moment(row._id).format("MM")),
        label: "Text count",
        data: graph?.map((row) => row?.total_text_count),
        backgroundColor: "rgba(255, 99, 132)",
        borderColor: "rgba(255, 99, 132,0.5)",
      },
    ],
  };

  const mdata = {
    labels: graph?.map((row) => moment(row.date).format("DD")),
    datasets: [
      {
        label: "Image Count",
        data: graph?.map((row) => row?.total_image_count),
        backgroundColor: "rgba(53, 162, 235)",
        borderColor: "rgba(53, 162, 235,0.5)",
      },
      {
        labels: graph?.map((row) => moment(row.date).format("DD")),
        label: "Text count",
        data: graph?.map((row) => row?.total_text_count),
        backgroundColor: "rgba(255, 99, 132)",
        borderColor: "rgba(255, 99, 132,0.5)",
      },
    ],
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              height="20.5rem"
            >
              {totalFlag ? (
                flag ? (
                  <Line data={data} options={options} />
                ) : (
                  <Line data={mdata} options={options} />
                )
              ) : (
                <Line data={ddata} options={options} />
              )}
            </MDBox>
          ),
          [chart, color]
        )}
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsBarChart
ReportsBarChart.defaultProps = {
  color: "dark",
  description: "",
};

// Typechecking props for the ReportsBarChart
ReportsBarChart.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  date: PropTypes.string.isRequired,
  chart: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.array, PropTypes.object])
  ).isRequired,
};

export default ReportsBarChart;
